// Attempt and round log in IndexedDB. Falls back to memory when IDB is unavailable.

import type { AttemptEvent } from './types';

export interface AttemptRow extends AttemptEvent { t: number; game: string; id?: number }
export interface RoundRow {
  id?: number; t: number; game: string; skill: string; level: number;
  attempts: number; correct: number; perfect: boolean; durationMs: number; gems: number; stars: number;
}

const DB = 'edugames';
const VER = 1;
let dbp: Promise<IDBDatabase | null> | null = null;
const mem: { attempts: AttemptRow[]; rounds: RoundRow[] } = { attempts: [], rounds: [] };

function open(): Promise<IDBDatabase | null> {
  if (dbp) return dbp;
  dbp = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB, VER);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('attempts')) {
          const s = db.createObjectStore('attempts', { keyPath: 'id', autoIncrement: true });
          s.createIndex('t', 't');
          s.createIndex('skill', 'skill');
        }
        if (!db.objectStoreNames.contains('rounds')) {
          const s = db.createObjectStore('rounds', { keyPath: 'id', autoIncrement: true });
          s.createIndex('t', 't');
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
  return dbp;
}

async function put(store: 'attempts' | 'rounds', row: any) {
  const db = await open();
  if (!db) { (mem[store] as any[]).push(row); return; }
  await new Promise<void>((res) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).add(row);
    tx.oncomplete = () => res();
    tx.onerror = () => res();
  });
}

export function logAttempt(game: string, e: AttemptEvent) {
  return put('attempts', { ...e, game, t: Date.now() });
}

export function logRound(r: Omit<RoundRow, 't'>) {
  return put('rounds', { ...r, t: Date.now() });
}

async function since<T>(store: 'attempts' | 'rounds', ms: number): Promise<T[]> {
  const db = await open();
  const cutoff = Date.now() - ms;
  if (!db) return (mem[store] as any[]).filter(r => r.t >= cutoff);
  return new Promise((res) => {
    const out: T[] = [];
    const tx = db.transaction(store, 'readonly');
    const idx = tx.objectStore(store).index('t');
    const req = idx.openCursor(IDBKeyRange.lowerBound(cutoff));
    req.onsuccess = () => {
      const c = req.result;
      if (c) { out.push(c.value); c.continue(); } else res(out);
    };
    req.onerror = () => res(out);
  });
}

export const attemptsSince = (ms: number) => since<AttemptRow>('attempts', ms);
export const roundsSince = (ms: number) => since<RoundRow>('rounds', ms);

export async function exportLog(): Promise<{ attempts: AttemptRow[]; rounds: RoundRow[] }> {
  const year = 1000 * 60 * 60 * 24 * 366 * 5;
  return { attempts: await attemptsSince(year), rounds: await roundsSince(year) };
}

export async function clearLog() {
  const db = await open();
  mem.attempts = []; mem.rounds = [];
  if (!db) return;
  await new Promise<void>((res) => {
    const tx = db.transaction(['attempts', 'rounds'], 'readwrite');
    tx.objectStore('attempts').clear();
    tx.objectStore('rounds').clear();
    tx.oncomplete = () => res();
    tx.onerror = () => res();
  });
}
